/**
 * Pure rules for the people who drive on a booking (FLEE-442).
 *
 * Dates are `YYYY-MM-DD` strings compared as strings: no `new Date(str)`, so
 * the runner's timezone can never shift a day. The server is the authority;
 * these rules only decide what the screens show and block before a request.
 */

/** Hard ceiling of additional drivers (the server clamps 0..5 as well). */
export const MAX_ADDITIONAL_DRIVERS = 5;

/** The fields a driver profile is judged on. Every key is optional on the wire. */
export interface RenterDriverFields {
  given_names?: string | null;
  last_name?: string | null;
  date_of_birth?: string | null;
  gender?: string | null;
  license_number?: string | null;
  license_number_last4?: string | null;
  license_expires_on?: string | null;
  license_street?: string | null;
  license_street_2?: string | null;
  license_city?: string | null;
  license_state?: string | null;
  license_zipcode?: string | null;
  license_country?: string | null;
  email?: string | null;
  phone?: string | null;
}

/** A field a driver must have before a renter can pay. */
export type RequiredDriverField =
  | "given_names"
  | "last_name"
  | "date_of_birth"
  | "license_number"
  | "license_expires_on"
  | "license_street"
  | "license_city"
  | "license_state"
  | "license_zipcode"
  | "license_country";

/**
 * Gender, street 2, email and phone are optional, so they are not listed.
 * Order is the order a form shows them.
 */
export const REQUIRED_DRIVER_FIELDS: readonly RequiredDriverField[] = [
  "given_names",
  "last_name",
  "date_of_birth",
  "license_number",
  "license_expires_on",
  "license_street",
  "license_city",
  "license_state",
  "license_zipcode",
  "license_country",
];

const DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;

const isBlank = (value: string | null | undefined) =>
  value == null || value.trim() === "";

/**
 * True when the license is still valid on the return day (same day counts).
 * False for a missing or malformed date.
 */
export function licenseValidThrough(
  expiresOn: string | null | undefined,
  returnDate: string | null | undefined,
): boolean {
  if (!expiresOn || !returnDate) return false;
  if (!DATE_PREFIX.test(expiresOn) || !DATE_PREFIX.test(returnDate)) {
    return false;
  }

  return expiresOn.slice(0, 10) >= returnDate.slice(0, 10);
}

/** Whether drivers must be captured: the fleet setting, or an option that checks every driver. */
export function driversRequired(
  config: { ask_at_checkout?: boolean } | null | undefined,
  selectedInsuranceOptions:
    | readonly { requires_all_drivers?: boolean }[]
    | null
    | undefined,
): boolean {
  if (config?.ask_at_checkout) return true;

  return (selectedInsuranceOptions ?? []).some(
    (option) => option.requires_all_drivers === true,
  );
}

/**
 * The required fields still blank. A masked profile (no "View driver details")
 * hides the full number and keeps the last four, which counts as present; its
 * DOB and address are hidden too, so for staff the server's `missing` list is
 * the truth and this one is for what the user typed in this browser.
 */
export function missingFields(
  driver: RenterDriverFields,
): RequiredDriverField[] {
  return REQUIRED_DRIVER_FIELDS.filter((key) => {
    if (key === "license_number") {
      return (
        isBlank(driver.license_number) && isBlank(driver.license_number_last4)
      );
    }

    return isBlank(driver[key]);
  });
}

/** Drivers that are complete and whose license is valid through the return. */
export function readyCount(
  drivers: readonly RenterDriverFields[],
  returnDate: string | null | undefined,
): number {
  return drivers.filter(
    (driver) =>
      missingFields(driver).length === 0 &&
      licenseValidThrough(driver.license_expires_on, returnDate),
  ).length;
}

/** The fleet's additional-driver limit, clamped to 0..5, default 5. */
export function maxAdditionalDrivers(
  config: { max_additional_drivers?: number | null } | null | undefined,
): number {
  const value = config?.max_additional_drivers;
  if (typeof value !== "number" || Number.isNaN(value)) {
    return MAX_ADDITIONAL_DRIVERS;
  }

  return Math.min(MAX_ADDITIONAL_DRIVERS, Math.max(0, Math.trunc(value)));
}

/**
 * Whether one more driver fits. `currentCount` includes the main driver, so
 * the cap is the main driver plus `max_additional_drivers`.
 */
export function canAddAnotherDriver(
  currentCount: number,
  config: { max_additional_drivers?: number | null } | null | undefined,
): boolean {
  return currentCount < 1 + maxAdditionalDrivers(config);
}
